import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, HashRouter, useNavigate, useLocation } from 'react-router-dom';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Box,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  Button,
  Divider,
  useMediaQuery,
  alpha,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Fade,
  useTheme,
  Grid
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  MoreVert as MoreVertIcon,
  GitHub as GitHubIcon,
  Twitter as TwitterIcon,
  Help as HelpIcon,
  Security as SecurityIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import EmailBox from './components/EmailBox';
import HomePage from './components/HomePage';
// Import page components
import FeaturesPage from './pages/FeaturesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AboutPage from './pages/AboutPage';
import StatusPage from './pages/StatusPage';
import CareersPage from './pages/CareersPage';
import PricingPage from './pages/PricingPage';
import BlogPage from './pages/BlogPage';
import CookiePage from './pages/CookiePage';
import './styles/animations.css'; // Import CSS animations
import Logo from './assets/1TimeMailLogo'; // Import our new logo component
import GoogleAd from './components/GoogleAd';

// Import iOS-style fonts
const sfProFontStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  
  @font-face {
    font-family: 'SF Pro Display';
    src: local('SF Pro Display'), 
         url('https://cdn.jsdelivr.net/gh/iamcal/Roboto-with-SF-Pro-Icons@main/dist/sf-pro-display_medium.woff2') format('woff2');
    font-weight: 500;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'SF Pro Display';
    src: local('SF Pro Display'), 
         url('https://cdn.jsdelivr.net/gh/iamcal/Roboto-with-SF-Pro-Icons@main/dist/sf-pro-display_semibold.woff2') format('woff2');
    font-weight: 600;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'SF Pro Display';
    src: local('SF Pro Display'), 
         url('https://cdn.jsdelivr.net/gh/iamcal/Roboto-with-SF-Pro-Icons@main/dist/sf-pro-display_bold.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
  }
`;

// Apply font styles
const style = document.createElement('style');
style.type = 'text/css';
style.appendChild(document.createTextNode(sfProFontStyle));
document.head.appendChild(style);

// Create the theme based on the mode
const createAppTheme = (mode: 'light' | 'dark') => {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#FFFFFF' : '#000000', // Black/white as primary
        dark: mode === 'dark' ? '#E0E0E0' : '#333333',
        light: mode === 'dark' ? '#F5F5F5' : '#666666',
        contrastText: mode === 'dark' ? '#000000' : '#FFFFFF', // Add explicit contrast text color
      },
      secondary: {
        main: mode === 'dark' ? '#121212' : '#F5F5F5', // Contrast colors
        dark: mode === 'dark' ? '#333333' : '#E0E0E0',
        light: mode === 'dark' ? '#666666' : '#FFFFFF',
        contrastText: mode === 'dark' ? '#FFFFFF' : '#000000', // Add explicit contrast text color
      },
      success: {
        main: mode === 'dark' ? '#CCCCCC' : '#444444', // Grayscale success
      },
      error: {
        main: mode === 'dark' ? '#FFFFFF' : '#000000', // Grayscale error
      },
      warning: {
        main: mode === 'dark' ? '#AAAAAA' : '#666666', // Grayscale warning
      },
      info: {
        main: mode === 'dark' ? '#DDDDDD' : '#333333', // Grayscale info
      },
      background: {
        default: mode === 'light' ? '#f8f9fa' : '#121212',
        paper: mode === 'light' ? '#ffffff' : '#1E1E1E',
      },
      grey: {
        50: mode === 'light' ? '#f8f9fa' : '#121212',
        100: mode === 'light' ? '#e9ecef' : '#1E1E1E',
        200: mode === 'light' ? '#dee2e6' : '#252525',
        300: mode === 'light' ? '#ced4da' : '#2c2c2c',
        400: mode === 'light' ? '#adb5bd' : '#3c3c3c',
        500: mode === 'light' ? '#6c757d' : '#6c757d',
        600: mode === 'light' ? '#495057' : '#adb5bd',
        700: mode === 'light' ? '#343a40' : '#ced4da',
        800: mode === 'light' ? '#212529' : '#dee2e6',
        900: mode === 'light' ? '#16191c' : '#e9ecef',
      },
      text: {
        primary: mode === 'light' ? 'rgba(29, 29, 29, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        secondary: mode === 'light' ? 'rgba(29, 29, 29, 0.6)' : 'rgba(255, 255, 255, 0.6)',
      }
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h1: {
        fontWeight: 800,
        fontSize: '2.5rem',
        letterSpacing: '-0.02em',
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
        letterSpacing: '-0.01em',
      },
      h3: {
        fontWeight: 700,
        fontSize: '1.75rem',
        letterSpacing: '-0.01em',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        letterSpacing: '0',
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        letterSpacing: '0',
      },
      h6: {
        fontWeight: 600,
        fontSize: '1.1rem',
        letterSpacing: '0',
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.7,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.7,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            transition: 'background-color 0.5s ease, color 0.5s ease',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            transition: 'background-color 0.5s ease, color 0.5s ease, border-color 0.5s ease',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.5s ease, box-shadow 0.5s ease',
            borderRadius: 12,
            boxShadow: mode === 'light'
              ? '0 5px 20px rgba(0, 0, 0, 0.05)'
              : '0 5px 20px rgba(0, 0, 0, 0.3)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            padding: '10px 24px',
            boxShadow: 'none',
            transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              boxShadow: mode === 'dark'
                ? '0 6px 15px rgba(255, 255, 255, 0.1)'
                : '0 6px 15px rgba(0, 0, 0, 0.1)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: mode === 'dark'
                ? '0 8px 20px rgba(255, 255, 255, 0.15)'
                : '0 8px 20px rgba(0, 0, 0, 0.15)',
            },
          },
        },
      },
    },
  });
};

// Enhanced modern header with better styling and functionality
const Header = ({ darkMode, toggleDarkMode }: { darkMode: boolean, toggleDarkMode: () => void }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backdropFilter: 'blur(8px)',
        backgroundColor: alpha(theme.palette.background.default, 0.8),
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ py: 1.5, justifyContent: 'space-between' }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Logo size="medium" />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                }}
              >
                1TimeMail
              </Typography>
            </Stack>
          </Link>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              component={Link}
              to="/features"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            >
              Features
            </Button>
            <Button
              component={Link}
              to="/how-it-works"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            >
              How It Works
            </Button>

            <Tooltip title={darkMode ? "Switch to light mode" : "Switch to dark mode"} arrow>
              <IconButton
                onClick={toggleDarkMode}
                size="small"
                color="inherit"
                sx={{
                  ml: { xs: 0, sm: 1 },
                  color: theme => theme.palette.mode === 'light' ? '#555555' : '#FFFFFF',
                  bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)',
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                {darkMode ?
                  <LightModeIcon sx={{ fontSize: '1.25rem' }} /> :
                  <DarkModeIcon sx={{ fontSize: '1.25rem' }} />
                }
              </IconButton>
            </Tooltip>

            <IconButton
              id="menu-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              size="small"
              color="inherit"
              sx={{
                display: { sm: 'none' },
                color: theme => theme.palette.mode === 'light' ? '#555555' : '#FFFFFF',
                '&:hover': {
                  bgcolor: theme => theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)'
                }
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Stack>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'menu-button',
            }}
          >
            <MenuItem onClick={handleClose} component={Link} to="/features">
              <InfoIcon fontSize="small" sx={{ mr: 1.5 }} />
              Features
            </MenuItem>
            <MenuItem onClick={handleClose} component={Link} to="/how-it-works">
              <HelpIcon fontSize="small" sx={{ mr: 1.5 }} />
              How It Works
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleClose} component={Link} to="/privacy">
              <SecurityIcon fontSize="small" sx={{ mr: 1.5 }} />
              Privacy Policy
            </MenuItem>
            <MenuItem onClick={handleClose} component={Link} to="/terms">
              <SecurityIcon fontSize="small" sx={{ mr: 1.5 }} />
              Terms of Service
            </MenuItem>
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

// Enhanced footer with better structure and additional information
const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        py: 4,
        backgroundColor: theme.palette.background.paper,
        borderTop: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: 4, lg: 6 },
          alignItems: { xs: 'start', lg: 'start' },
          justifyContent: 'space-between'
        }}>
          {/* Brand Section */}
          <Box sx={{
            flex: { lg: '1 1 40%' },
            maxWidth: { lg: '400px' },
            width: { xs: '100%', lg: 'auto' }
          }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit', width: 'fit-content', display: 'block' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Logo size="medium" />
                <Typography variant="h6" sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                }}>
                  1TimeMail
                </Typography>
              </Stack>
            </Link>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              1TimeMail is your trusted solution for temporary email needs. We provide instant, secure, and disposable email addresses to protect your privacy online.
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                component="a"
                href="https://github.com/1timemail/securefakemail"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <GitHubIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                component="a"
                href="https://twitter.com/1timemail"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <TwitterIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          {/* Navigation Links */}
          <Box sx={{
            flex: { lg: '1 1 60%' },
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(4, 1fr)'
            },
            gap: { xs: 3, sm: 4 },
            width: '100%'
          }}>
            {/* Product Column */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                gutterBottom
                sx={{
                  color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                  mb: 2
                }}
              >
                Product
              </Typography>
              <Stack spacing={1.5}>
                <Link to="/features" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    Features
                  </Typography>
                </Link>
                <Link to="/how-it-works" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    How It Works
                  </Typography>
                </Link>
                <Link to="/pricing" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    Pricing
                  </Typography>
                </Link>
              </Stack>
            </Box>

            {/* Support Column */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                gutterBottom
                sx={{
                  color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                  mb: 2
                }}
              >
                Support
              </Typography>
              <Stack spacing={1.5}>
                <Link to="/faq" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    FAQ
                  </Typography>
                </Link>
                <Link to="/contact" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    Contact
                  </Typography>
                </Link>
                <Link to="/status" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    System Status
                  </Typography>
                </Link>
              </Stack>
            </Box>

            {/* Legal Column */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                gutterBottom
                sx={{
                  color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                  mb: 2
                }}
              >
                Legal
              </Typography>
              <Stack spacing={1.5}>
                <Link to="/privacy" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    Privacy Policy
                  </Typography>
                </Link>
                <Link to="/terms" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    Terms of Service
                  </Typography>
                </Link>
                <Link to="/cookies" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    Cookie Policy
                  </Typography>
                </Link>
              </Stack>
            </Box>

            {/* Company Column */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                gutterBottom
                sx={{
                  color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                  mb: 2
                }}
              >
                Company
              </Typography>
              <Stack spacing={1.5}>
                <Link to="/about" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    About Us
                  </Typography>
                </Link>
                <Link to="/blog" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    Blog
                  </Typography>
                </Link>
                <Link to="/careers" style={{ textDecoration: 'none' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        transition: 'color 0.2s ease'
                      }
                    }}
                  >
                    Careers
                  </Typography>
                </Link>
              </Stack>
            </Box>
          </Box>
        </Box>

        {/* Footer Bottom */}
        <Divider sx={{ my: 4 }} />

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            © {currentYear} 1TimeMail. All rights reserved.
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            alignItems={{ xs: 'start', sm: 'center' }}
          >
            <Link to="/privacy" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  '&:hover': {
                    color: 'primary.main',
                    transition: 'color 0.2s ease'
                  }
                }}
              >
                Privacy
              </Typography>
            </Link>
            <Link to="/terms" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  '&:hover': {
                    color: 'primary.main',
                    transition: 'color 0.2s ease'
                  }
                }}
              >
                Terms
              </Typography>
            </Link>
            <Link to="/cookies" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  '&:hover': {
                    color: 'primary.main',
                    transition: 'color 0.2s ease'
                  }
                }}
              >
                Cookies
              </Typography>
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

const currentYear = new Date().getFullYear();

interface PageTransitionProps {
  children: React.ReactNode;
}

const PageTransition: React.FC<PageTransitionProps> = ({ children }) => {
  return (
    <Fade in timeout={200}>
      <Box>{children}</Box>
    </Fade>
  );
};

function App() {
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);

    // Update the data-theme attribute on the HTML element
    document.documentElement.setAttribute('data-theme', !darkMode ? 'dark' : 'light');

    // Add animation classes for theme toggle effect
    const themeButtonIcon = document.querySelector('.theme-toggle-button-icon');
    if (themeButtonIcon) {
      themeButtonIcon.classList.remove('theme-switch-light', 'theme-switch-dark');

      // Force reflow to restart animation
      void (themeButtonIcon as HTMLElement).offsetWidth;

      if (darkMode) {
        // Switching to light
        themeButtonIcon.classList.add('theme-switch-light');
      } else {
        // Switching to dark
        themeButtonIcon.classList.add('theme-switch-dark');
      }
    }
  };

  // Save dark mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
    // Set the data-theme attribute on the HTML element
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Check if we should use hash routing (for GitHub Pages and other static hosts)
  const useHashRouter = process.env.REACT_APP_USE_HASH_ROUTER === 'true' || window.location.hostname.includes('github.io');

  // Create the theme
  const theme = createAppTheme(darkMode ? 'dark' : 'light');

  // Router content with layout
  const AppContent = ({ darkMode, toggleDarkMode }: { darkMode: boolean; toggleDarkMode: () => void }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Handle redirect from 404.html
    useEffect(() => {
      // Check for inbox-specific redirect parameter
      const urlParams = new URLSearchParams(location.search);
      const redirectTo = urlParams.get('redirect_to');

      if (redirectTo) {
        console.log('Inbox redirect detected, navigating to:', redirectTo);
        urlParams.delete('redirect_to');
        const newSearch = urlParams.toString();
        const newPath = location.pathname + (newSearch ? '?' + newSearch : '');

        // Navigate to the inbox URL
        navigate(redirectTo, { replace: true });
        return;
      }

      // Check if we have a redirect path from a page refresh
      const redirectPath = sessionStorage.getItem('redirectPath');
      if (redirectPath && redirectPath !== location.pathname && !redirectPath.startsWith('/?redirect=')) {
        console.log('Redirecting to:', redirectPath);
        sessionStorage.removeItem('redirectPath');
        // Use navigate instead of window.location for a proper React Router navigation
        navigate(redirectPath, { replace: true });
      } else if (redirectPath) {
        // Clear the redirect path if we're already on the correct path
        sessionStorage.removeItem('redirectPath');
      }

      // Clean up redirect parameter from URL if present
      if (location.search.includes('redirect=')) {
        const urlParams = new URLSearchParams(location.search);
        urlParams.delete('redirect');
        const newSearch = urlParams.toString();
        const newPath = location.pathname + (newSearch ? '?' + newSearch : '');
        if (newPath !== location.pathname + location.search) {
          navigate(newPath, { replace: true });
        }
      }
    }, [navigate, location]);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          transition: 'background-color 0.5s ease',
        }}
      >
        <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

        <Box sx={{ flexGrow: 1 }}>
          <Routes>
            <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
            <Route path="/inbox/:email" element={<PageTransition><EmailBox /></PageTransition>} />

            {/* New routes for the created pages */}
            <Route path="/features" element={<PageTransition><FeaturesPage /></PageTransition>} />
            <Route path="/how-it-works" element={<PageTransition><HowItWorksPage /></PageTransition>} />
            <Route path="/faq" element={<PageTransition><FAQPage /></PageTransition>} />
            <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
            <Route path="/privacy" element={<PageTransition><PrivacyPage /></PageTransition>} />
            <Route path="/terms" element={<PageTransition><TermsPage /></PageTransition>} />
            <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
            <Route path="/status" element={<PageTransition><StatusPage /></PageTransition>} />
            <Route path="/careers" element={<PageTransition><CareersPage /></PageTransition>} />
            <Route path="/pricing" element={<PageTransition><PricingPage /></PageTransition>} />
            <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
            <Route path="/cookies" element={<PageTransition><CookiePage /></PageTransition>} />

            {/* Add a catch-all route that redirects to home */}
            <Route path="*" element={<PageTransition><HomePage /></PageTransition>} />
          </Routes>
        </Box>

        <Footer />
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {useHashRouter ? (
        <HashRouter>
          <AppContent darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        </HashRouter>
      ) : (
        <BrowserRouter>
          <AppContent darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        </BrowserRouter>
      )}
    </ThemeProvider>
  );
}

export default App;
